import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PageHeaderProps {
  title?: string;
  description?: string;
  onBack?: () => void;
  titleTestId?: string;
  descriptionTestId?: string;
  leftSlot?: React.ReactNode;
  centerSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
  children?: React.ReactNode;
  layout?: 'default' | 'centered';
  className?: string;
}

export function PageHeader({
  title,
  description,
  onBack,
  titleTestId,
  descriptionTestId,
  leftSlot,
  centerSlot,
  rightSlot,
  children,
  layout = 'default',
  className,
}: PageHeaderProps) {
  const backButton = onBack ? (
    <button
      type="button"
      onClick={onBack}
      className="p-2 hover:bg-slate-100 rounded-lg transition-all duration-200"
    >
      <ArrowLeft className="w-6 h-6 text-slate-700" />
    </button>
  ) : null;

  const titleBlock =
    title || description ? (
      <div>
        {title ? (
          <h1
            className="text-2xl font-bold text-slate-900"
            data-testid={titleTestId}
          >
            {title}
          </h1>
        ) : null}
        {description ? (
          <p className="text-slate-600 text-sm" data-testid={descriptionTestId}>
            {description}
          </p>
        ) : null}
      </div>
    ) : null;

  return (
    <div className={cn('page-header', className)}>
      <div
        className={cn(
          'flex items-center justify-between max-w-7xl mx-auto',
          layout === 'centered' && 'gap-2',
        )}
      >
        {layout === 'centered' ? (
          <>
            {backButton ?? <div className="w-10" />}
            <div className="flex-1 text-center">
              {centerSlot ??
                (title || description ? (
                  <div>
                    {title ? (
                      <h1 className="text-lg font-bold text-slate-900">
                        {title}
                      </h1>
                    ) : null}
                    {description ? (
                      <p className="text-xs text-slate-500 mt-0.5">
                        {description}
                      </p>
                    ) : null}
                  </div>
                ) : null)}
            </div>
            {rightSlot ? (
              <div className="flex items-center gap-3">{rightSlot}</div>
            ) : (
              <div className="w-10" />
            )}
          </>
        ) : (
          <>
            {leftSlot ?? (
              <div className="flex items-center gap-3">
                {backButton}
                {titleBlock}
              </div>
            )}
            {rightSlot ? (
              <div className="flex items-center gap-2">{rightSlot}</div>
            ) : null}
          </>
        )}
      </div>
      {children}
    </div>
  );
}
